import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, HelpCircle, Clock, Award, CheckCircle } from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import { eCourseService } from "../../services/elearning/eCourseService";

interface StudentStats {
  enrolledCourses: number;
  completedCourses: number;
  inProgressCourses: number;
  averageGrade: number;
  totalQuizzes: number;
  completedQuizzes: number;
  certificatesEarned: number;
  studyHours: number;
}

interface RecentActivity {
  id: string;
  type: "course" | "quiz" | "achievement";
  title: string;
  description: string;
  timestamp: Date;
  status: "completed" | "in_progress" | "pending" | "overdue";
}

interface UpcomingDeadline {
  id: string;
  type: "quiz" | "course";
  title: string;
  courseName: string;
  dueDate: Date;
  priority: "high" | "medium" | "low";
}

const StudentElearningDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<StudentStats>({
    enrolledCourses: 0,
    completedCourses: 0,
    inProgressCourses: 0,
    averageGrade: 0,
    totalQuizzes: 0,
    completedQuizzes: 0,
    certificatesEarned: 0,
    studyHours: 0,
  });

  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState<
    UpcomingDeadline[]
  >([]);

  // Fetch enrolled courses
  const { data: enrolledCoursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ["student-enrolled-courses"],
    queryFn: () => eCourseService.getMyEnrolledCourses(),
  });

  const enrolledCourses = enrolledCoursesData?.data || [];

  // Calculate stats from real data
  useEffect(() => {
    if (enrolledCourses.length > 0) {
      const completedCourses = enrolledCourses.filter(
        (enrollment: any) => enrollment.progress === 100
      ).length;
      const inProgressCourses = enrolledCourses.filter(
        (enrollment: any) =>
          enrollment.progress > 0 && enrollment.progress < 100
      ).length;
      const averageProgress =
        enrolledCourses.reduce(
          (sum: number, enrollment: any) => sum + enrollment.progress,
          0
        ) / enrolledCourses.length;

      setStats({
        enrolledCourses: enrolledCourses.length,
        completedCourses,
        inProgressCourses,
        averageGrade: averageProgress,
        totalQuizzes: 15, // This would come from quizzes API
        completedQuizzes: 12, // This would come from quizzes API
        certificatesEarned: completedCourses, // Assuming one certificate per completed course
        studyHours: 45, // This would come from tracking API
      });
    } else {
      // Default stats when no courses enrolled
      setStats({
        enrolledCourses: 0,
        completedCourses: 0,
        inProgressCourses: 0,
        averageGrade: 0,
        totalQuizzes: 0,
        completedQuizzes: 0,
        certificatesEarned: 0,
        studyHours: 0,
      });
    }

    // Generate recent activity from enrolled courses
    if (enrolledCourses.length > 0) {
      const activities: RecentActivity[] = enrolledCourses
        .slice(0, 3)
        .map((enrollment: any) => ({
          id: enrollment._id,
          type: "course",
          title: enrollment.course.name,
          description: `Progress: ${enrollment.progress}% completed`,
          timestamp: new Date(
            enrollment.lastAccessedAt || enrollment.enrolledAt
          ),
          status: enrollment.progress === 100 ? "completed" : "in_progress",
        }));

      setRecentActivity(activities);
    }

    setUpcomingDeadlines([
      {
        id: "2",
        type: "quiz",
        title: "CSS Grid and Flexbox Quiz",
        courseName: "Frontend Fundamentals",
        dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000),
        priority: "medium",
      },
    ]);
  }, [enrolledCourses]);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "course":
        return <BookOpen className="w-4 h-4" />;
      case "quiz":
        return <HelpCircle className="w-4 h-4" />;
      case "achievement":
        return <Award className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-gradient-to-r from-green-500 to-emerald-500 text-white";
      case "in_progress":
        return "bg-gradient-to-r from-blue-500 to-indigo-500 text-white";
      case "pending":
        return "bg-gradient-to-r from-yellow-500 to-orange-500 text-white";
      case "overdue":
        return "bg-gradient-to-r from-red-500 to-pink-500 text-white";
      default:
        return "bg-gradient-to-r from-slate-500 to-slate-600 text-white";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-gradient-to-br from-red-50 to-pink-50 border border-red-200/50";
      case "medium":
        return "bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200/50";
      case "low":
        return "bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/50";
      default:
        return "bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200/50";
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else {
      return "Just now";
    }
  };

  const formatDueDate = (date: Date) => {
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return "Due today";
    } else if (days === 1) {
      return "Due tomorrow";
    } else if (days > 0) {
      return `Due in ${days} days`;
    } else {
      return "Overdue";
    }
  };

  if (coursesLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
            E-Learning Dashboard
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-2">
            Track your progress and continue learning
          </p>
        </div>
        <button
          onClick={() => navigate("/student/elearning/catalog")}
          className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 font-semibold"
        >
          <BookOpen className="w-5 h-5" />
          Browse Courses
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold text-slate-900">
              {stats.enrolledCourses}
            </p>
            <p className="text-sm text-slate-600 font-medium mt-1">
              Enrolled Courses
            </p>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
              <CheckCircle className="w-7 h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold text-slate-900">
              {stats.enrolledCourses > 0
                ? Math.round(
                    (stats.completedCourses / stats.enrolledCourses) * 100
                  )
                : 0}
              %
            </p>
            <p className="text-sm text-slate-600 font-medium mt-1">
              Completion Rate
            </p>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center shadow-md">
              <Award className="w-7 h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold text-slate-900">
              {Math.round(stats.averageGrade)}%
            </p>
            <p className="text-sm text-slate-600 font-medium mt-1">
              Average Progress
            </p>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
              <Clock className="w-7 h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-3xl font-bold text-slate-900">
              {stats.studyHours}
            </p>
            <p className="text-sm text-slate-600 font-medium mt-1">
              Study Hours
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-lg transition-all duration-300">
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                Recent Activity
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex items-start gap-3 p-4 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <div
                      className={`p-2.5 rounded-xl flex-shrink-0 shadow-sm ${getStatusColor(
                        activity.status
                      )}`}
                    >
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-semibold text-slate-900 break-words">
                        {activity.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 break-words mt-1">
                        {activity.description}
                      </p>
                      <p className="text-xs text-slate-500 mt-2 font-medium">
                        {formatTimeAgo(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-slate-200">
                <button className="text-blue-600 hover:text-blue-700 text-sm font-semibold hover:underline">
                  View all activity
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-lg transition-all duration-300">
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                Upcoming Deadlines
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {upcomingDeadlines.map((deadline) => (
                  <div
                    key={deadline.id}
                    className={`p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 ${getPriorityColor(
                      deadline.priority
                    )}`}
                  >
                    <h3 className="text-sm font-semibold text-slate-900 break-words">
                      {deadline.title}
                    </h3>
                    <p className="text-xs text-slate-600 break-words mt-1">
                      {deadline.courseName}
                    </p>
                    <p className="text-xs text-slate-500 mt-2 font-medium">
                      {formatDueDate(deadline.dueDate)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-lg transition-all duration-300">
        <div className="px-6 py-5 border-b border-slate-200">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
            Quick Actions
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => navigate("/student/elearning/my-courses")}
              className="p-5 text-left rounded-2xl hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/50 group hover:scale-105"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md flex-shrink-0 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    My E-Courses
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Continue your enrolled courses
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/student/elearning/catalog")}
              className="p-5 text-left rounded-2xl hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/50 group hover:scale-105"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md flex-shrink-0 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    Browse Courses
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Discover new learning opportunities
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => navigate("/student/elearning/quizzes")}
              className="p-5 text-left rounded-2xl hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200/50 group hover:scale-105"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-md flex-shrink-0 group-hover:scale-110 transition-transform">
                  <HelpCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    Take Quizzes
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600">
                    Test your knowledge
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentElearningDashboard;
